import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { NowOrderTracking } from '../components/now/NowOrderTracking';
import { ColdChainStatus, TelemetryStatus } from '../components/delivery/ColdChainStatus';
import { PackageIcon, TruckIcon, CheckCircleIcon, MapPinIcon, PhoneIcon, HomeIcon, ChevronRightIcon } from 'lucide-react';
export function TrackOrderPage() {
  const [searchParams] = useSearchParams();
  const screenInit = useScreenInit();
  const isNowMode = searchParams.get('mode') === 'now' || screenInit.mode === 'now';
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [trackingData, setTrackingData] = useState<any>(null);
  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    const hasTemperatureException = orderNumber.toUpperCase().includes('ISSUE');
    const telemetryStatus: TelemetryStatus = hasTemperatureException ? 'exception' : 'verified';
    // Simulated tracking contract — replace telemetry fields with live logistics data when available.
    setTrackingData({
      orderNumber: orderNumber,
      status: 'Out for Delivery',
      estimatedDelivery: 'Today, 2:00 PM - 5:00 PM',
      trackingNumber: 'TRK123456789',
      courier: 'Innova Express',
      courierPhone: '+966 920 000 000',
      coldChain: true,
      telemetryStatus,
      temperature: hasTemperatureException ? 9.4 : 5.1,
      temperatureRange: '2–8°C',
      telemetryUpdatedAt: 'Just now',
      vehicleLabel: 'Temperature-controlled vehicle · 4821',
      timeline: [{
        status: 'Order Confirmed',
        description: 'Your order has been received and confirmed',
        timestamp: 'Jan 15, 2024 - 10:30 AM',
        completed: true
      }, {
        status: 'Processing',
        description: 'Your order is being prepared for shipment',
        timestamp: 'Jan 15, 2024 - 11:00 AM',
        completed: true
      }, {
        status: 'Shipped',
        description: 'Your order has been dispatched',
        timestamp: 'Jan 15, 2024 - 3:00 PM',
        completed: true
      }, {
        status: 'Out for Delivery',
        description: 'Your order is on the way to your location',
        timestamp: 'Jan 16, 2024 - 9:00 AM',
        completed: true,
        active: true
      }, {
        status: 'Delivered',
        description: 'Order delivered successfully',
        timestamp: 'Pending',
        completed: false
      }],
      items: [{
        name: 'Panadol Extra Pain Relief Tablets 24s',
        quantity: 2,
        image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop'
      }, {
        name: 'Vitamin D3 Supplement',
        quantity: 1,
        image: 'https://images.unsplash.com/photo-1550572017-4bfa2d5c8f1d?w=400&h=400&fit=crop'
      }],
      deliveryAddress: 'King Fahd Road, Al Olaya, Riyadh 12211, Saudi Arabia'
    });
  };
  if (isNowMode) return <NowOrderTracking data-id="element-9429" data-mp="ym7t2kaf4dvr" />;
  return <HelmetProvider data-id="element-9430" data-mp="13jhgjue8yfgy">
      <SEO title="Track Your Order" description="Track your Innova Pharmacy order in real-time. Enter your order number to see the current status and estimated delivery time." canonical="/track-order" data-id="element-9431" data-mp="13iogfc9smo7s" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-9432" data-mp="kx4ocq0rgvy6">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-9433" data-mp="zl91zfkyiha3">
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-9434" data-mp="unh7cdsxgh27" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-9435" data-mp="6yes1clrn0av" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-9436" data-mp="bx97q5ofs8qr">
          {/* Mobile Header Overlay - Only on mobile */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-9437" data-mp="1sdc6mq6ex1uv">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-9438" data-mp="1nbd0x9n79sur" />
          </div>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Track Order'
        }]} title="Track Your Order" description="Enter your order number to track your delivery" data-id="element-9439" data-mp="1lv8yw4o0uswg" />
          <div className="container mx-auto px-4 py-8" data-id="element-9440" data-mp="2ikgn5kg0zg42">
            <div className="max-w-4xl mx-auto" data-id="element-9441" data-mp="24ud412cg02iu">
              {!trackingData ? <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8" data-id="element-9442" data-mp="28v6uo3h9rvff">
                  <div className="text-center mb-8" data-id="element-9443" data-mp="2wl17il1l9jzo">
                    <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-9444" data-mp="2wjgc6t7g9x0r">
                      <PackageIcon className="w-10 h-10 text-[#1E2B55]" data-id="element-9445" data-mp="31dub35lbmma6" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-9446" data-mp="3b5m2c2q4au4q">
                      Track Your Order
                    </h2>
                    <p className="text-gray-600" data-id="element-9447" data-mp="326iigwj4map6">
                      Enter your order number to see real-time tracking
                      information
                    </p>
                  </div>
                  <form onSubmit={handleTrack} className="max-w-md mx-auto space-y-6" data-id="element-9448" data-mp="2s9dv4tdjo96s">
                    <div data-id="element-9449" data-mp="2nguflqvar3zc">
                      <label htmlFor="orderNumber" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9450" data-mp="2jgjz3k9vov68">
                        Order Number
                      </label>
                      <input type="text" id="orderNumber" value={orderNumber} onChange={e => setOrderNumber(e.target.value)} placeholder="e.g., ORD-12345" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-9451" data-mp="29hbbra9iqesu" />
                    </div>
                    <button type="submit" className="w-full bg-[#1E2B55] text-white font-bold py-3 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-9452" data-mp="29bu45ycw6rbi">
                      Track Order
                    </button>
                  </form>
                  <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-9453" data-mp="1llq39yr395o7">
                    <h3 className="font-semibold text-gray-900 mb-2" data-id="element-9454" data-mp="1qfaun7y6f1ef">
                      Where to find your order number?
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-2" data-id="element-9455" data-mp="xx6rh98tyard">
                      <li className="flex items-start" data-id="element-9456" data-mp="xv3u245clfcm">
                        <span className="text-blue-600 mr-2" data-id="element-9457" data-mp="2c4yfeahnrwuf">•</span>
                        Check your order confirmation email
                      </li>
                      <li className="flex items-start" data-id="element-9458" data-mp="2m232qdn8pyct">
                        <span className="text-blue-600 mr-2" data-id="element-9459" data-mp="2m3nww25n7fa5">•</span>
                        Visit your account dashboard under "My Orders"
                      </li>
                      <li className="flex items-start" data-id="element-9460" data-mp="2v29dhrrk0k8a">
                        <span className="text-blue-600 mr-2" data-id="element-9461" data-mp="2v0oenpy7if4m">•</span>
                        Check the SMS confirmation sent to your phone
                      </li>
                    </ul>
                  </div>
                </div> : <div className="space-y-6" data-id="element-9462" data-mp="2zuiwsoy5ym1l">
                  {/* Order Status Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-9463" data-mp="3epyvj79ov64b">
                    <div className="flex items-start justify-between mb-6" data-id="element-9464" data-mp="39s7228tebby0">
                      <div data-id="element-9465" data-mp="3tdh1sa2wg8su">
                        <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-9466" data-mp="3td7dcqxi69p9">
                          Order {trackingData.orderNumber}
                        </h2>
                        <p className="text-gray-600" data-id="element-9467" data-mp="3jf9pi4jk2ex6">
                          Tracking: {trackingData.trackingNumber}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-9468" data-mp="3ocjddfraqiiw">
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium" data-id="element-9469" data-mp="39p7zh77n6mfm">
                          <TruckIcon className="w-4 h-4" data-id="element-9470" data-mp="3ehhnghp75cf9" />
                          {trackingData.status}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg" data-id="element-9471" data-mp="35oea09ejwzkm">
                      <div data-id="element-9472" data-mp="2qr5ed3i7t9n3">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-9473" data-mp="2qsyfy4dz13o8">
                          Estimated Delivery
                        </p>
                        <p className="font-semibold text-gray-900" data-id="element-9474" data-mp="2xutv0nreiiv6">
                          {trackingData.estimatedDelivery}
                        </p>
                      </div>
                      <div data-id="element-9475" data-mp="2ny7ay16wfvu7">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-9476" data-mp="2nyz6xd4ervl0">Courier</p>
                        <p className="font-semibold text-gray-900" data-id="element-9477" data-mp="2svz2gu3mtfxv">
                          {trackingData.courier}
                        </p>
                      </div>
                      <div data-id="element-9478" data-mp="3llg1wbpo5x3l">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-9479" data-mp="3llpk3d4h2zyq">Contact</p>
                        <a href={`tel:${trackingData.courierPhone}`} className="font-semibold text-[#1E2B55] hover:underline" data-id="element-9480" data-mp="36wtcfh6bvoyn">
                          {trackingData.courierPhone}
                        </a>
                      </div>
                    </div>
                  </div>
                  {trackingData.coldChain && <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm" aria-label="Cold-chain delivery status" data-id="element-9481" data-mp="1swf2ey32q51o">
                      <div className="mb-3 flex items-center justify-between gap-3" data-id="element-9482" data-mp="1nxc7w7kpzh7y"><p className="text-sm font-bold text-gray-900" data-id="element-9483" data-mp="22n9pu5s23aop">Temperature-protected delivery</p><span className="text-xs font-semibold text-gray-500" data-id="element-9484" data-mp="1xnopbqx8mu9v">{trackingData.vehicleLabel}</span></div>
                      <ColdChainStatus status={trackingData.telemetryStatus} temperature={trackingData.temperature} temperatureRange={trackingData.temperatureRange} lastUpdated={trackingData.telemetryUpdatedAt} data-id="element-9485" data-mp="19xkplun67eo5" />
                      {trackingData.telemetryStatus === 'exception' && <p className="mt-3 text-xs leading-5 text-rose-700" data-id="element-9486" data-mp="1iwo8axifds24">Your order remains protected while a pharmacist confirms the next safe step. We will contact you before any delivery change.</p>}
                    </section>}
                  {/* Tracking Timeline */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-9487" data-mp="2qef6uo9qg90l">
                    <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-9488" data-mp="1n16os23iwojn">
                      Tracking Timeline
                    </h3>
                    <div className="space-y-6" data-id="element-9489" data-mp="1wzw2diodzmu3">
                      {trackingData.timeline.map((event: any, index: number) => <div key={index} className="flex gap-4" data-id="element-9490" data-mp="210erh4ywaesl" data-mpi={index}>
                            <div className="flex flex-col items-center" data-id="element-9491" data-mp="14c08elq7c910">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${event.completed ? event.active ? 'bg-blue-100 animate-pulse' : 'bg-green-100' : 'bg-gray-100'}`} data-id="element-9492" data-mp="199ifs7okmg6w">
                                <CheckCircleIcon className={`w-6 h-6 ${event.completed ? event.active ? 'text-blue-600' : 'text-green-600' : 'text-gray-400'}`} data-id="element-9493" data-mp="1i46mm29so5xt" />
                              </div>
                              {index < trackingData.timeline.length - 1 && <div className={`w-0.5 h-16 my-2 ${event.completed ? 'bg-green-200' : 'bg-gray-200'}`} data-id="element-9494" data-mp="d36y047uo6eh"></div>}
                            </div>
                            <div className="flex-1 pb-4" data-id="element-9495" data-mp="453d9a4ovdnq">
                              <h4 className={`font-semibold ${event.completed ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-9496" data-mp="3v9tpzydz9rpn">
                                {event.status}
                              </h4>
                              <p className={`text-sm ${event.completed ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-9497" data-mp="3hhwz1xo1pnuu">
                                {event.description}
                              </p>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-9498" data-mp="37lkafe7cy06y">
                                {event.timestamp}
                              </p>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  {/* Order Items */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-9499" data-mp="16ouhvxncy9ti">
                    <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-9500" data-mp="splejyk93a6y">
                      Order Items
                    </h3>
                    <div className="space-y-4" data-id="element-9501" data-mp="wsztsn0d7mjc">
                      {trackingData.items.map((item: any, index: number) => <div key={index} className="flex items-center gap-4" data-id="element-9502" data-mp="iu089o85fnre" data-mpi={index}>
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0" data-id="element-9503" data-mp="nr0cdtlfugfv">
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-9504" data-mp="2d3lnufrc8kue" />
                          </div>
                          <div className="flex-1" data-id="element-9505" data-mp="1je5gyy74qu7">
                            <h4 className="font-medium text-gray-900" data-id="element-9506" data-mp="1gi8bcu6a66t">
                              {item.name}
                            </h4>
                            <p className="text-sm text-gray-600" data-id="element-9507" data-mp="bfhiizul69vx">
                              Quantity: {item.quantity}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>
                  {/* Delivery Address */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-9508" data-mp="z5bu7e365sel">
                    <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-9509" data-mp="u7jptklc8dti">
                      Delivery Address
                    </h3>
                    <div className="flex items-start gap-3" data-id="element-9510" data-mp="2vydcssxnnxbj">
                      <MapPinIcon className="w-5 h-5 text-[#1E2B55] flex-shrink-0 mt-1" data-id="element-9511" data-mp="2vzy8wmzmbobi" />
                      <p className="text-gray-700" data-id="element-9512" data-mp="3lnu2q93qvjpe">
                        {trackingData.deliveryAddress}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setTrackingData(null)} className="w-full border-2 border-[#1E2B55] text-[#1E2B55] font-bold py-3 rounded-lg hover:bg-[#F1F2F6] transition-colors" data-id="element-9513" data-mp="3um5tqc8iwjid">
                    Track Another Order
                  </button>
                </div>}
            </div>
          </div>
        </main>
        <Footer data-id="element-9514" data-mp="h815ptbdd9m9" />
        <MobileFooterNav data-id="element-9515" data-mp="h9lwd7ukc8eu" />
        <PharmacistChat data-id="element-9516" data-mp="hadt4lzwcc6p" />
        <div className="h-16 md:hidden" data-id="element-9517" data-mp="m749zbjizbt6"></div>
      </div>
    </HelmetProvider>;
}